import React from "react";
import { Platform, StyleSheet, View, type ViewStyle } from "react-native";
import { ArrowLeftIcon, LockSimpleIcon } from "@phosphor-icons/react";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";

interface NoPermissionPanelProps {
  onGoBack: () => void;
  title?: string;
  description?: string;
}

function NoPermissionPanel({
  onGoBack,
  title = "Access Denied",
  description = "You do not have permission to view this page. This course review belongs to another faculty member.",
}: NoPermissionPanelProps) {
  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <CustomButton
          text="Back to Course Modernizer"
          styleType="transparent"
          size="s"
          useTranslationText={false}
          onPress={onGoBack}
          style={styles.backButton}
          textStyle={styles.backButtonText}
          leftIcon={<ArrowLeftIcon size={18} color={colors.grey[700]} />}
        />
      </View>

      <View style={styles.content}>
        <View style={styles.card}>
          <View style={styles.iconCircle}>
            <LockSimpleIcon size={32} color={colors.grey[400]} />
          </View>
          <CustomText text={title} useTranslationText={false} size="l" weight="bold" style={styles.title} />
          <CustomText
            text={description}
            useTranslationText={false}
            size="s"
            weight="normal"
            style={styles.description}
          />
          <CustomButton
            text="Back to Course Modernizer"
            styleType="primary"
            size="s"
            useTranslationText={false}
            onPress={onGoBack}
            style={styles.actionButton}
            textStyle={styles.actionButtonText}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    ...(Platform.OS === "web"
      ? ({
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
        } as unknown as ViewStyle)
      : {}),
  },
  topBar: {
    height: 56,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  backButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  backButtonText: {
    color: colors.grey[700],
    fontSize: 12,
    fontWeight: "500",
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: colors.grey[50],
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    borderStyle: "dashed",
    padding: 48,
    alignItems: "center",
    maxWidth: 480,
    width: "100%",
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.grey[100],
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  title: {
    color: colors.grey[900],
    marginBottom: 8,
    textAlign: "center",
  },
  description: {
    color: colors.grey[500],
    textAlign: "center",
    lineHeight: 22,
    marginBottom: 24,
  },
  actionButton: {
    backgroundColor: "#3E68FE",
    borderRadius: 6,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  actionButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
  },
});

export default NoPermissionPanel;
